import {api, useApiQuery} from '@/api';
import utils from 'utils';

import {useEffect, useState, useRef} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import {useLocation, useNavigate} from 'react-router-dom';

import {Header, SidePanel, Detail} from '@components/export';

export const OrderListPage = (props: {
    mode: 'product' | 'order',
}) => {
    // fixme?: refactor

    const pathname = window.location.pathname;

    const queryClient = useQueryClient();
    const locationState = useLocation().state;
    const navigate = useNavigate();

    const [mode, setMode] = useState(props.mode);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [loadingSide, setLoadingSide] = useState(true);
    const [currentItem, setCurrentItem] = useState(null);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [hideDone, setHideDone] = useState(false);
    const [showPrevious, setShowPrevious] = useState(false);
    const [doSubmit, setDoSubmit] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    const [queryString, setQueryString] = useState('');

    const locked = useRef(false);

    // fixme: move to hook - use order query
    // dont forget related state vars
    const query = {
        order: useApiQuery(
            'getOrder',
            {
                orderId: queryString,
            },
            !queryString || props.mode !== 'order',
        ),
        product: useApiQuery(
            'getProduct',
            {
                productName: queryString,
                currentDate: currentDate,
            },
            !queryString || props.mode !== 'product',
        ),
        orders: useApiQuery(
            'getOrders',
            {
                currentDate: currentDate,
                showPrevious: showPrevious,
            },
            props.mode !== 'order',
        ),
        products: useApiQuery(
            'getProducts',
            {
                currentDate: currentDate,
            },
            props.mode !== 'product',
        ),
        additionalPayments: useApiQuery(
            'getAdditionalPayments',
            {},
        ),
    };

    const handle = {
        submitChanges() {
            new Promise((resolve) => {
                if (props.mode === 'order') {
                    resolve(
                        api.panas.updateOrder(currentItem.id, {
                            lines: currentItem.lines.map((line) => {
                                return {
                                    id: line.id,
                                    price: line.price,
                                    isDone: line.isDone,
                                    deleted: !!line.deleted,
                                    note: line.note,
                                };
                            }),
                            note: currentItem.note,
                            readyForCollect: currentItem.readyForCollect,
                        }).then(() => {
                            return queryClient.invalidateQueries({queryKey: ['order']});
                        }).then(() => {
                            return queryClient.invalidateQueries({queryKey: ['orders']});
                        }),
                    );
                }

                if (props.mode === 'product') {
                    resolve(
                        api.panas.updateProducts({
                            lines: currentItem.lines.map((line) => {
                                return {
                                    id: line.id,
                                    price: line.price,
                                    isDone: line.isDone,
                                    note: line.note,
                                };
                            }),
                        }).then(() => {
                            return queryClient.invalidateQueries({queryKey: ['product']});
                        }).then(() => {
                            return queryClient.invalidateQueries({queryKey: ['products']});
                        }),
                    );
                }
            })
                .then(() => {
                    locked.current = false;
                    setDoSubmit(false);
                });
        },
        getItemList() {
            if (query.orders.data !== undefined && props.mode === 'order') {
                return handle.sortItemList(query.orders.data['data'])
                    .filter((item) => {
                        return (!hideDone ? true : (item.qtyToDo !== item.qtyDone)) &&
                                item.qtyToDo &&
                                (
                                    !searchQuery ? true :
                                        handle.cleanDiacritics(item.accountName).includes(handle.cleanDiacritics(searchQuery)) ||
                                        handle.cleanDiacritics(item.customerName).includes(handle.cleanDiacritics(searchQuery)) ||
                                        handle.cleanDiacritics(item.orderNum).includes(handle.cleanDiacritics(searchQuery))
                                )
                    });
            }

            if (query.products.data !== undefined && props.mode === 'product') {
                return handle.sortItemList(query.products.data['data'])
                    .filter((item) => {
                        return (!hideDone ? true : (item.qtyToDo !== item.qtyDone)) &&
                            item.qtyToDo &&
                            (
                                !searchQuery ? true :
                                    handle.cleanDiacritics(item.name).includes(handle.cleanDiacritics(searchQuery))
                            )
                    });
            }

            return [];
        },
        cleanDiacritics(string) {
            return string.normalize('NFD').replace(/[\u0300-\u036f]/g, "").toLowerCase();
        },
        getCurrentIndex() {
            const itemList = handle.getItemList();
            let index = -1;

            if (props.mode === 'order') {
                index = itemList.findIndex((item) => item.id === queryString);
            }

            if (props.mode === 'product') {
                index = itemList.findIndex((item) => item.name === queryString);
            }

            return index < 0 ? null : index;
        },
        updateQueryStringByIndex(index: number) {
            const itemList = handle.getItemList();

            if (props.mode === 'order') {
                const orderId = itemList[index]?.id;

                if (!orderId) {
                    return;
                }

                setQueryString(orderId);
            }

            if (props.mode === 'product') {
                const productName = itemList[index]?.name;

                if (!productName) {
                    return;
                }

                setQueryString(productName);
            }
        },
        sortItemList(list) {
            return list.sort((a, b) => {
                const dateA = a.dateCreated?.split(' ')[0];
                const dateB = b.dateCreated?.split(' ')[0];

                // eslint-disable-next-line jsdoc/require-jsdoc
                const getGroupPriority = (item) => {
                    if (item.qtyDone !== 0 && item.qtyDone < item.qtyToDo) return 1;
                    if (item.qtyDone === 0) return 2;
                    if (item.qtyDone === item.qtyToDo) return 3;
                    return 4;
                };

                if (dateA !== dateB) {
                    return dateA > dateB ? -1 : 1;
                }

                if (getGroupPriority(a) !== getGroupPriority(b)) {
                    return getGroupPriority(a) - getGroupPriority(b);
                }

                if (mode === 'product') {
                    return a.name > b.name ? 1 : -1;
                } else if (mode === 'order') {
                    return a.orderNum > b.orderNum ? 1 : -1;
                }

                return 1;
            });
        },
    };

    useEffect(() => {
        const sortedBy = pathname.split('/').at(-1);

        if (sortedBy === 'byOrders') {
            setQueryString(locationState?.id);
            setMode('order');
        }

        if (sortedBy === 'byProducts') {
            setQueryString('');
            setMode('product');
        }
    }, [pathname]);

    useEffect(() => {
        setCurrentItem(null);
    }, [props.mode, currentDate, hideDone]);

    useEffect(() => {
        setHideDone(false);
        setShowPrevious(false);
    }, [props.mode]);

    useEffect(() => {
        if (props.mode === 'order') {
            setLoadingDetail(query.order.isFetching);
            setLoadingSide(query.orders.isFetching);

            if (query.order.data !== undefined && !doSubmit) {
                setCurrentItem(query.order.data);
            }
        }

        if (props.mode === 'product') {
            setLoadingDetail(query.product.isFetching);
            setLoadingSide(query.products.isFetching);

            if (
                query.product.data !== undefined &&
                !doSubmit &&
                query.product.data['data'].name
            ) {
                setCurrentItem(query.product.data['data']);
            }
        }
    }, [query]);

    useEffect(() => {
        if (doSubmit) {
            handle.submitChanges();
        }
    }, [doSubmit]);

    return <>
        <Header
            onRefresh={() => {
                queryClient.invalidateQueries({queryKey: ['order']});
                queryClient.invalidateQueries({queryKey: ['orders']});
                queryClient.invalidateQueries({queryKey: ['product']});
                queryClient.invalidateQueries({queryKey: ['products']});
            }}
        />

        <div className={'page-content ' + cmpClass}>
            <div className={classMap.wrapper}>
                <SidePanel
                    loading={loadingSide || locked.current}
                    mode={mode}
                    itemList={handle.getItemList()}
                    showDate={showPrevious}
                    currentIndex={handle.getCurrentIndex()}
                    currentDate={currentDate}
                    searchQuery={searchQuery}
                    flags={{
                        hideDone,
                        showPrevious,
                    }}

                    onDateChange={(date) => {
                        setCurrentDate(date);
                        setQueryString('');
                        setCurrentItem(null);
                    }}
                    onItemClick={(index) => handle.updateQueryStringByIndex(index)}
                    onHideDone={(value) => {
                        setHideDone(value);
                        setCurrentItem(null);
                    }}
                    onShowPrevious={(value) => {
                        setShowPrevious(value);
                        setCurrentItem(null);
                    }}
                    onSearch={(query) => {
                        setSearchQuery(query);
                    }}
                />
                <Detail
                    mode={mode}
                    item={currentItem}
                    loading={loadingDetail || locked.current}
                    additionalPaymentList={
                        query.additionalPayments.data !== undefined && Array.isArray(query.additionalPayments.data) ?
                            query.additionalPayments.data : []
                    }

                    onItemFieldUpdate={(field, value) => {
                        locked.current = true;

                        setDoSubmit(true);
                        setCurrentItem({
                            ...currentItem,
                            [field]: value,
                        });
                    }}

                    onItemLineUpdate={(field, value, id) => {
                        locked.current = true;

                        setDoSubmit(true);
                        setCurrentItem({
                            ...currentItem,
                            lines: [
                                ...currentItem.lines.map((line) => {
                                    if (line.id === id) {
                                        line[field] = value;
                                    }

                                    return {...line};
                                }),
                            ],
                        });
                    }}

                    onAdditionalPaymentAdd={(lineId, paymentId) => {
                        locked.current = true;

                        setLoadingDetail(true);
                        setLoadingSide(true);

                        api.panas.updateOrder(currentItem.id, {
                            additionalPayment: {
                                paymentId,
                                lineId,
                            },
                        }).then(() => {
                            return queryClient.invalidateQueries({queryKey: ['order']});
                        }).then(() => {
                            locked.current = false;
                            setLoadingDetail(false);
                            setLoadingSide(false);
                        });
                    }}

                    onItemGoToOrder={(id) => {
                        navigate('/orders/byOrders', {
                            state: {id: id},
                        });
                    }}
                />
            </div>
        </div>
    </>;
};

const cmpClass = 'order-list-page';
const classMap = utils.generateClassMap(cmpClass, {
    wrapper: 'wrapper',
});
